import React, { useEffect, useRef, useState } from 'react';
import { EmailFormFields } from './EmailFormFields';
import { useForm } from 'react-hook-form';
import { ASSETS_URL } from '../../components/Store';
import { Link, useLocation } from 'react-router-dom';
import { useId } from '../../hooks/use-id';
import ReCAPTCHA from 'react-google-recaptcha';
import { CancelRequestController } from '../../util/cancel-request-controller';
import { useQueryParams } from '../../hooks/useQueryParams';
import { DatosFormInstitutoAPI } from '../../services/api/LoginInstitucional';
import MicroModal from 'micromodal';
import ModalAlertas from '../../components/ModalAlertas';
import { useInstitutionalEmailSubmit } from '../hooks/useInstitutionalEmailSubmit';
import { isDevelopment } from '../util/isDevelopment';

/**
 * Formulario de ingreso institucional mediante correo electrónico.
 *
 * Este componente renderiza el formulario utilizado para que un usuario
 * solicite acceso a una institución. Administra el estado del formulario
 * mediante React Hook Form, valida los campos, controla el reCAPTCHA
 * (cuando no se encuentra en ambiente de desarrollo) y envía la información
 * al servicio correspondiente.
 *
 * En caso de que la validación del servicio lo requiera, muestra un modal
 * informativo al usuario; de lo contrario, realiza la redirección indicada
 * por la respuesta del backend.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.datosInstituto - Información de la institución, como nombre y logo.
 * @param {Object} props.traducciones - Textos internacionalizados utilizados en la interfaz.
 *
 * @returns {JSX.Element} FormEmail
 */
export const FormEmail = ({ datosInstituto, traducciones }) => {
    const { institucional, login, accesibilidad } = traducciones;
    const { form } = institucional;
    const { nombreMostrar, logo } = datosInstituto?.data || {};

    const formController = useForm({ mode: "all" });

    const captcha = useRef(null);
    const [captchaValido, setCaptchaValido] = useState(null);
    const captchaHelp = useId('captcha-help');

    const isDevelopmentEnvironment = isDevelopment();
    const [sendingData, setSendingData] = useState(false);
    const location = useLocation();
    const params = useQueryParams();
    const redirect = params.get('redirectto');

    const [mostrarModal, setMostrarModal] = useState(false);

    const { submitForm } = useInstitutionalEmailSubmit({
        datosInstituto,
        onShowModal: () => {
            if (!mostrarModal) {
                setMostrarModal(true);
            }

            MicroModal.show("modal-form-institucional", {
                awaitCloseAnimation: true
            });
        }
    });

    const onSubmitFormHandler = async (data, e) => {
        e.preventDefault();

        // Validar captcha si no está en desarrollo
        if (!isDevelopmentEnvironment && captchaValido !== true) {
            console.warn('Captcha no validado');
            return;
        }

        try {
            setSendingData(true);
            await submitForm(data);
        } catch (error) {
            console.error('Error al enviar:', error);
        } finally {
            setSendingData(false);
        }
    }

    /**
   * Esta función cambia el estado de setCaptchaValido en caso de que el captcha sea correcto
   */
    const onChange = () => {
        if (captcha.current.getValue()) {
            setCaptchaValido(true)
        }
    }

    return (
        <>
            <div className="col-lg-9 mx-auto pt-3 pb-5">
                <img
                    src={`${ASSETS_URL}${logo || '/img/logo-makemake.png'}`}
                    alt={""}
                    aria-hidden={true}
                    className='mx-auto d-block'
                    style={{ maxWidth: '300px' }}
                />
                <h1
                    className="my-3 text-center pb-3 secondary-item text-24"
                    style={{ textTransform: 'capitalize' }}
                >
                    {nombreMostrar || 'Nombre del instituto'}
                </h1>


                <hr aria-hidden="true" />

                <form
                    className="row pt-3 mx-0 my-3 align-items-end"
                    name="InstitutionalFormEmail"
                    onSubmit={(e) => {
                        e.preventDefault();
                        formController.handleSubmit(
                            onSubmitFormHandler,
                            (errors) => {
                                console.log('🚫 NO SE PUEDE ENVIAR - Errores encontrados:');
                                Object.entries(errors).forEach(([field, error]) => {
                                    console.log(`  ❌ ${field}:`, error?.message || error?.type);
                                });
                            }
                        )(e);
                    }}
                >
                    <fieldset className="col-lg-6 mb-3 mb-lg-0">
                        <EmailFormFields.InstIdTypeField formController={formController} texts={traducciones} />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0">
                        <EmailFormFields.InstIdField formController={formController} texts={traducciones} />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
                        <EmailFormFields.InstFullNameInput formController={formController} texts={traducciones} />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
                        <EmailFormFields.InstEmailInput formController={formController} texts={traducciones} />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
                        <EmailFormFields.InstDepartmentField formController={formController} texts={traducciones} />
                    </fieldset>

                    <p className="mx-auto my-5 my-lg-0 p-2 p-lg-5 text-center mm-redimir-terms">
                        {form?.terminos?.ingresar || 'Al ingresar tus datos estás aceptando'}
                        <Link
                            className="links-tyc secondary-item focusable-red"
                            to="/terminos-y-condiciones"
                        >
                            {" "}{form?.terminos?.term_condiciones || 'Términos y condiciones'}
                        </Link>
                        {" "}{form?.terminos?.y || 'y'} {" "}
                        <Link
                            className="links-tyc secondary-item focusable-red"
                            to="/politica-de-privacidad"
                        >
                            {form?.terminos?.politica || 'Política de privacidad'}
                        </Link>
                    </p>

                    {!isDevelopmentEnvironment && (
                        <>
                            <div
                                aria-describedby={captchaHelp}
                                className="w-100 d-flex justify-content-center"
                            >
                                <ReCAPTCHA
                                    ref={captcha}
                                    sitekey="6LfaebskAAAAAF4jRB9WbC3pOOgmAaPZnOf-kn5-"
                                    onChange={onChange}
                                />
                            </div>
                            <p
                                id={captchaHelp} className="sr-only">
                                {accesibilidad?.captcha || "Verifica que no eres un robot"}
                            </p>
                        </>

                    )}

                    <button
                        className={`btn col-6 col-lg-4 d-block mx-auto focusable-red mt-5 ${sendingData ? 'btn-disabled' : 'btn-primary'}`}
                        type="submit"
                        disabled={sendingData ? true : false}
                    >
                        {sendingData ?
                            <>
                                <span className="sr-only">
                                    {accesibilidad?.enviando || "Enviando formulario"}
                                </span>
                                <i className="fas fa-spinner fa-pulse" aria-hidden={true}></i>
                            </>
                            :
                            login?.botones?.enviar || "Enviar"
                        }
                    </button>
                </form>
            </div>

            {mostrarModal &&
                <ModalAlertas
                    id={"modal-form-institucional"}
                    alertType={form?.modal?.titulo || 'Alerta'}
                    message={form?.modal?.mensaje || 'Error de acceso'}
                    description={
                        form?.modal?.descripcion_doc || 'El nombre ingresado no coincide con el del documento'
                    }
                    boton={login?.botones?.aceptar || "Aceptar"} />
            }
        </>
    )
}